import { useState, useEffect } from 'react'
import { PortalLayout } from '../components/PortalLayout'
import { api } from '../api'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar,
} from 'recharts'

type Tab = 'overview' | 'hours' | 'missing' | 'patterns' | 'calendar' | 'debrief' | 'audit'

const URGENCY_COLORS: Record<string, string> = {
  exceeded: '#dc2626', critical: '#dc2626', overdue: '#dc2626',
  low: '#f59e0b', warning: '#f59e0b',
  moderate: '#3b82f6', upcoming: '#3b82f6',
  good: '#10b981', ok: '#10b981', excellent: '#10b981',
  unknown: '#9ca3af',
}

const GRADE_COLORS: Record<string, string> = {
  A: '#10b981', B: '#22c55e', C: '#f59e0b', D: '#f97316', E: '#ef4444', F: '#dc2626',
}

export function IntelligencePage() {
  const [tab, setTab] = useState<Tab>('overview')
  const [riskData, setRiskData] = useState<any>(null)
  const [hoursData, setHoursData] = useState<any>(null)
  const [missingData, setMissingData] = useState<any>(null)
  const [patternsData, setPatternsData] = useState<any>(null)
  const [calendarData, setCalendarData] = useState<any>(null)
  const [benchmarkData, setBenchmarkData] = useState<any>(null)
  const [auditData, setAuditData] = useState<any>(null)
  const [debriefData, setDebriefData] = useState<any>(null)
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null)
  const [allDrivers, setAllDrivers] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // ── Depot filter + search + pagination ─────────────────────
  const [depots, setDepots] = useState<any[]>([])
  const [selectedDepot, setSelectedDepot] = useState<number | undefined>(undefined)
  const [search, setSearch] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [riskPage, setRiskPage] = useState(1)
  const [hoursPage, setHoursPage] = useState(1)

  // Load depots on mount
  useEffect(() => {
    api.getDepots().then(setDepots).catch(() => {})
  }, [])

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300)
    return () => clearTimeout(t)
  }, [search])

  // Reload data when depot/search changes
  useEffect(() => { loadOverview() }, [selectedDepot, searchDebounced])

  async function loadOverview() {
    setLoading(true)
    setError('')
    const params = { depot_id: selectedDepot, search: searchDebounced || undefined, page: riskPage, page_size: 50 }
    try {
      const [risk, hours, missing, benchmarks] = await Promise.all([
        api.getFleetRiskScore(params).catch(() => null),
        api.getFleetHoursRemaining({ ...params, page: hoursPage }).catch(() => null),
        api.getMissingDataAlerts(params).catch(() => null),
        api.getFleetBenchmarks(selectedDepot).catch(() => null),
      ])
      if (risk) setRiskData(risk)
      if (hours) setHoursData(hours)
      if (missing) setMissingData(missing)
      if (benchmarks) setBenchmarkData(benchmarks)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  async function loadPatterns() {
    setLoading(true)
    try {
      const data = await api.getFleetPatterns(selectedDepot)
      setPatternsData(data)
    } catch (e: any) { setError(e.message) }
    finally { setLoading(false) }
  }

  async function loadCalendar() {
    setLoading(true)
    try {
      const data = await api.getComplianceCalendar(selectedDepot)
      setCalendarData(data)
    } catch (e: any) { setError(e.message) }
    finally { setLoading(false) }
  }

  async function loadAudit() {
    setLoading(true)
    try {
      const data = await api.getAuditPack(selectedDepot)
      setAuditData(data)
    } catch (e: any) { setError(e.message) }
    finally { setLoading(false) }
  }

  async function loadDebrief(driverId: number) {
    setLoading(true)
    setSelectedDriverId(driverId)
    try {
      const data = await api.getDriverDebrief(driverId)
      setDebriefData(data)
    } catch (e: any) { setError(e.message) }
    finally { setLoading(false) }
  }

  async function loadAllDrivers() {
    try {
      const data = await api.getDrivers({ page_size: 1000, depot_id: selectedDepot })
      const list = Array.isArray(data) ? data : data.drivers || []
      setAllDrivers(list.map((d: any) => ({ driver_id: d.id, driver_name: `${d.first_names || ''} ${d.surname || ''}`.trim(), score: null, grade: null })))
    } catch { /* fallback to risk data drivers */ }
  }

  function handleTabChange(t: Tab) {
    setTab(t)
    if (t === 'patterns') loadPatterns()
    if (t === 'calendar') loadCalendar()
    if (t === 'audit') loadAudit()
    if (t === 'debrief' && allDrivers.length === 0) loadAllDrivers()
  }

  function handleDepotChange(depotId: string) {
    setSelectedDepot(depotId ? Number(depotId) : undefined)
    setRiskPage(1)
    setHoursPage(1)
    // Clear cached sub-tab data so it reloads with new depot
    setPatternsData(null)
    setCalendarData(null)
    setAuditData(null)
  }

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'overview', label: 'Risk Overview', icon: '🛡️' },
    { id: 'hours', label: 'Hours Remaining', icon: '⏱️' },
    { id: 'missing', label: 'Missing Data', icon: '📋' },
    { id: 'patterns', label: 'Patterns', icon: '📊' },
    { id: 'calendar', label: 'Compliance Calendar', icon: '📅' },
    { id: 'debrief', label: 'Driver Debrief', icon: '💬' },
    { id: 'audit', label: 'DVSA Audit Pack', icon: '📦' },
  ]

  return (
    <PortalLayout currentPage="intelligence">
      <div className="p-5">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-900">Intelligence Engine</h1>
            <p className="text-gray-500 mt-1">Predictive compliance analytics, risk scoring, and audit-ready reporting</p>
          </div>
          {/* Depot filter + search */}
          <div className="flex items-center gap-3">
            {depots.length > 0 && (
              <select
                value={selectedDepot ?? ''}
                onChange={e => handleDepotChange(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              >
                <option value="">All Depots</option>
                {depots.map((d: any) => (
                  <option key={d.id} value={d.id}>{d.name} ({d.driver_count} drivers)</option>
                ))}
              </select>
            )}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search drivers..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 border border-gray-300 rounded text-sm w-56 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">
            {error}
            <button onClick={() => setError('')} className="ml-2 font-medium underline">Dismiss</button>
          </div>
        )}

        {/* Tab pills */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-4">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => handleTabChange(t.id)}
              className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                tab === t.id
                  ? 'bg-slate-700 text-white shadow-sm'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span className="mr-1.5">{t.icon}</span>{t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="text-center py-12 text-gray-400">
            <div className="animate-spin w-8 h-8 border-4 border-slate-400 border-t-transparent rounded-full mx-auto mb-3" />
            Loading intelligence data...
          </div>
        )}

        {!loading && tab === 'overview' && <OverviewPanel risk={riskData} benchmarks={benchmarkData} page={riskPage} onPageChange={(p: number) => { setRiskPage(p); loadOverview() }} />}
        {!loading && tab === 'hours' && <HoursPanel data={hoursData} page={hoursPage} onPageChange={(p: number) => { setHoursPage(p); loadOverview() }} />}
        {!loading && tab === 'missing' && <MissingDataPanel data={missingData} />}
        {!loading && tab === 'patterns' && <PatternsPanel data={patternsData} />}
        {!loading && tab === 'calendar' && <CalendarPanel data={calendarData} />}
        {!loading && tab === 'debrief' && (
          <DebriefPanel
            data={debriefData}
            drivers={allDrivers.length > 0 ? allDrivers : (riskData?.drivers || [])}
            onSelectDriver={loadDebrief}
            selectedId={selectedDriverId}
          />
        )}
        {!loading && tab === 'audit' && <AuditPanel data={auditData} />}
      </div>
    </PortalLayout>
  )
}


/* ═══════════════════════════════════════════════════
   OVERVIEW PANEL — Fleet Risk Score + Benchmarks
   ═══════════════════════════════════════════════════ */
function OverviewPanel({ risk, benchmarks, page, onPageChange }: { risk: any; benchmarks: any; page: number; onPageChange: (p: number) => void }) {
  if (!risk) return <EmptyState msg="No risk data available yet. Ensure drivers have card downloads." />

  const distData = [
    { name: 'Critical (<40)', value: risk.distribution.critical, fill: '#ef4444' },
    { name: 'At Risk (40-69)', value: risk.distribution.at_risk, fill: '#f59e0b' },
    { name: 'Good (70-89)', value: risk.distribution.good, fill: '#3b82f6' },
    { name: 'Excellent (90+)', value: risk.distribution.excellent, fill: '#10b981' },
  ].filter(d => d.value > 0)

  return (
    <div className="space-y-6">
      {/* Fleet score hero */}
      <div className="bg-white rounded border border-gray-200 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Score gauge */}
          <div className="flex flex-col items-center justify-center">
            <div className="relative w-40 h-40">
              <svg viewBox="0 0 160 160" className="w-full h-full">
                <circle cx="80" cy="80" r="70" fill="none" stroke="#f3f4f6" strokeWidth="12" />
                <circle
                  cx="80" cy="80" r="70" fill="none"
                  stroke={GRADE_COLORS[risk.fleet_grade] || '#6b7280'}
                  strokeWidth="12"
                  strokeDasharray={`${(risk.fleet_score / 100) * 440} 440`}
                  strokeLinecap="round"
                  transform="rotate(-90 80 80)"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-bold" style={{ color: GRADE_COLORS[risk.fleet_grade] }}>{risk.fleet_score}</span>
                <span className="text-sm text-gray-400">/ 100</span>
              </div>
            </div>
            <p className="mt-2 text-lg font-semibold">Fleet Compliance Score</p>
            <span className={`mt-1 text-sm px-3 py-1 rounded-full font-medium`} style={{ backgroundColor: GRADE_COLORS[risk.fleet_grade] + '20', color: GRADE_COLORS[risk.fleet_grade] }}>
              Grade {risk.fleet_grade}
            </span>
          </div>

          {/* Distribution pie */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Risk Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={distData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                  {distData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <Tooltip formatter={(v: number) => `${v} drivers`} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend verticalAlign="bottom" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Quick stats */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Fleet Summary</h3>
            <StatRow label="Total Drivers" value={risk.total_drivers} />
            <StatRow label="Critical Risk" value={risk.distribution.critical} color={risk.distribution.critical > 0 ? '#ef4444' : undefined} />
            <StatRow label="At Risk" value={risk.distribution.at_risk} color={risk.distribution.at_risk > 0 ? '#f59e0b' : undefined} />
            <StatRow label="Good/Excellent" value={risk.distribution.good + risk.distribution.excellent} color="#10b981" />
            {benchmarks?.metrics && (
              <>
                <div className="border-t border-gray-100 pt-2 mt-2" />
                <StatRow label="Avg Infringements/Driver" value={benchmarks.metrics.avg_infringements_per_driver} />
                <StatRow label="Avg Driving Hours" value={`${benchmarks.metrics.avg_driving_hours_per_driver}h`} />
                <StatRow label="Infringement Rate/Day" value={benchmarks.metrics.infringement_rate_per_day} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Driver score cards */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Driver Risk Scores</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {risk.drivers.map((d: any) => (
            <div key={d.driver_id} className={`bg-white rounded border p-4 ${d.score < 40 ? 'border-red-300 bg-red-50' : d.score < 70 ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900 truncate">{d.driver_name}</span>
                <span
                  className="text-lg font-bold"
                  style={{ color: GRADE_COLORS[d.grade] || '#6b7280' }}
                >
                  {d.score}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{ width: `${d.score}%`, backgroundColor: GRADE_COLORS[d.grade] || '#6b7280' }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Grade {d.grade}</span>
                <span>{d.recent_infringements} recent inf.</span>
                <span>DCRS L{d.dcrs_level}</span>
              </div>
            </div>
          ))}
        {risk.pagination && risk.pagination.total_pages > 1 && (
          <PaginationBar pagination={risk.pagination} onPageChange={onPageChange} />
        )}
        </div>
      </div>
    </div>
  )
}


/* ═══════════════════════════════════════════════════
   HOURS REMAINING PANEL
   ═══════════════════════════════════════════════════ */
function HoursPanel({ data, page, onPageChange }: { data: any; page: number; onPageChange: (p: number) => void }) {
  if (!data) return <EmptyState msg="No hours data available." />

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Total Drivers</p>
          <p className="text-3xl font-bold text-gray-900">{data.total_drivers}</p>
        </div>
        <div className={`rounded border p-5 ${data.drivers_near_limit > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <p className="text-sm text-gray-500">Near Limit (&lt;1h)</p>
          <p className={`text-3xl font-bold ${data.drivers_near_limit > 0 ? 'text-red-700' : 'text-green-700'}`}>{data.drivers_near_limit}</p>
        </div>
        <div className="bg-blue-50 rounded border border-blue-200 p-5">
          <p className="text-sm text-gray-500">Available Drivers</p>
          <p className="text-3xl font-bold text-blue-700">{data.total_drivers - data.drivers_near_limit}</p>
        </div>
      </div>

      {/* Driver hours table */}
      <div className="bg-white rounded border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-500">Driver</th>
              <th className="text-center px-3 py-3 font-medium text-gray-500">Daily</th>
              <th className="text-center px-3 py-3 font-medium text-gray-500">Weekly</th>
              <th className="text-center px-3 py-3 font-medium text-gray-500">Fortnightly</th>
              <th className="text-center px-3 py-3 font-medium text-gray-500">Continuous</th>
              <th className="text-center px-3 py-3 font-medium text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.drivers.map((d: any) => (
              <tr key={d.driver_id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{d.driver_name}</td>
                {d.data_available ? (
                  <>
                    <td className="text-center px-3 py-3">
                      <HoursBar used={d.daily_driving_used_mins} limit={d.daily_limit_mins} />
                    </td>
                    <td className="text-center px-3 py-3">
                      <HoursBar used={d.weekly_driving_used_mins} limit={3360} />
                    </td>
                    <td className="text-center px-3 py-3">
                      <HoursBar used={d.fortnight_driving_used_mins} limit={5400} />
                    </td>
                    <td className="text-center px-3 py-3">
                      <HoursBar used={270 - d.continuous_driving_remaining_mins} limit={270} />
                    </td>
                    <td className="text-center px-3 py-3">
                      <UrgencyBadge urgency={d.urgency} />
                    </td>
                  </>
                ) : (
                  <td colSpan={5} className="text-center px-3 py-3 text-gray-400">No data</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.pagination && data.pagination.total_pages > 1 && (
        <PaginationBar pagination={data.pagination} onPageChange={onPageChange} />
      )}
    </div>
  )
}

function HoursBar({ used, limit }: { used: number; limit: number }) {
  const pct = Math.min(100, (used / limit) * 100)
  const color = pct > 90 ? '#ef4444' : pct > 75 ? '#f59e0b' : '#10b981'
  const remaining = Math.max(0, limit - used)
  return (
    <div className="flex flex-col items-center">
      <div className="w-full bg-gray-200 rounded-full h-2 mb-1" style={{ maxWidth: 100 }}>
        <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs text-gray-500">{(remaining / 60).toFixed(1)}h left</span>
    </div>
  )
}


/* ═══════════════════════════════════════════════════
   MISSING DATA PANEL
   ═══════════════════════════════════════════════════ */
function MissingDataPanel({ data }: { data: any }) {
  if (!data) return <EmptyState msg="No missing data alerts." />

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <AlertCard label="Drivers Overdue" value={data.summary.drivers_overdue} color="red" />
        <AlertCard label="Drivers Critical" value={data.summary.drivers_critical} color="yellow" />
        <AlertCard label="Vehicles Overdue" value={data.summary.vehicles_overdue} color="red" />
        <AlertCard label="Action Required" value={data.summary.total_alerts} color={data.summary.total_alerts > 0 ? 'red' : 'green'} />
      </div>

      {/* Driver card downloads */}
      <div className="bg-white rounded border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Driver Card Downloads (28-day rule)</h3>
        <div className="space-y-2">
          {data.driver_alerts.map((a: any) => (
            <div key={a.id} className={`flex items-center justify-between p-3 rounded border ${
              a.urgency === 'overdue' ? 'bg-red-50 border-red-200' :
              a.urgency === 'critical' ? 'bg-red-50 border-red-200' :
              a.urgency === 'warning' ? 'bg-yellow-50 border-yellow-200' :
              'bg-gray-50 border-gray-200'
            }`}>
              <div>
                <p className="text-sm font-medium">{a.name}</p>
                <p className="text-xs text-gray-500">
                  {a.last_download ? `Last: ${new Date(a.last_download).toLocaleDateString('en-GB')}` : 'Never downloaded'}
                  {a.days_since_download != null && ` (${a.days_since_download} days ago)`}
                </p>
              </div>
              <div className="text-right">
                {a.days_remaining != null ? (
                  <div>
                    <span className={`text-lg font-bold ${a.days_remaining < 0 ? 'text-red-600' : a.days_remaining <= 7 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {a.days_remaining < 0 ? `${Math.abs(a.days_remaining)}d overdue` : `${a.days_remaining}d left`}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">No data</span>
                )}
                <UrgencyBadge urgency={a.urgency} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Vehicle unit downloads */}
      {data.vehicle_alerts.length > 0 && (
        <div className="bg-white rounded border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Vehicle Unit Downloads (90-day rule)</h3>
          <div className="space-y-2">
            {data.vehicle_alerts.map((a: any) => (
              <div key={a.id} className="flex items-center justify-between p-3 rounded border border-gray-200 bg-gray-50">
                <div>
                  <p className="text-sm font-medium">{a.registration}</p>
                  <p className="text-xs text-gray-500">{a.make} {a.model}</p>
                </div>
                <UrgencyBadge urgency={a.urgency} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}


/* ═══════════════════════════════════════════════════
   PATTERNS PANEL
   ═══════════════════════════════════════════════════ */
function PatternsPanel({ data }: { data: any }) {
  if (!data) return <EmptyState msg="Loading pattern data..." />

  const typeBreakdown = Object.entries(data.type_breakdown || {}).map(([name, value]) => ({
    name: name.replace(/_/g, ' '), value: value as number,
  })).sort((a, b) => b.value - a.value)

  const dowBreakdown = Object.entries(data.day_of_week_breakdown || {}).map(([name, value]) => ({
    name: name.slice(0, 3), value: value as number,
  }))

  const COLORS = ['#4c6ef5', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4']

  return (
    <div className="space-y-6">
      {/* Insights */}
      {data.insights?.length > 0 && (
        <div className="bg-indigo-50 rounded border border-indigo-200 p-5">
          <h3 className="text-sm font-semibold text-indigo-700 mb-2">Fleet Insights</h3>
          <div className="space-y-1">
            {data.insights.map((ins: string, i: number) => (
              <p key={i} className="text-sm text-indigo-600">• {ins}</p>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Infringement types */}
        <div className="bg-white rounded border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Infringement Types</h3>
          {typeBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={typeBreakdown} layout="vertical" margin={{ left: 120 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="value" fill="#4c6ef5" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">No infringement data</p>
          )}
        </div>

        {/* Day of week */}
        <div className="bg-white rounded border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Infringements by Day of Week</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dowBreakdown}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {dowBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}


/* ═══════════════════════════════════════════════════
   COMPLIANCE CALENDAR PANEL
   ═══════════════════════════════════════════════════ */
function CalendarPanel({ data }: { data: any }) {
  if (!data) return <EmptyState msg="Loading compliance calendar..." />

  const [filter, setFilter] = useState<string>('all')
  const categories = ['all', 'mot', 'tax', 'tacho_cal', 'insurance', 'pmi', 'speed_limiter', 'card_download']

  const filtered = filter === 'all' ? data.events : data.events.filter((e: any) => e.category === filter)

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <AlertCard label="Overdue" value={data.summary.overdue} color="red" />
        <AlertCard label="Critical (7 days)" value={data.summary.critical} color="yellow" />
        <AlertCard label="Warning (30 days)" value={data.summary.warning} color="blue" />
        <AlertCard label="Total Events" value={data.summary.total_events} color="gray" />
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map(c => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
              filter === c ? 'bg-slate-700 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {c === 'all' ? 'All' : c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Events list */}
      <div className="bg-white rounded border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-500">Type</th>
              <th className="text-left px-3 py-3 font-medium text-gray-500">Entity</th>
              <th className="text-left px-3 py-3 font-medium text-gray-500">Category</th>
              <th className="text-center px-3 py-3 font-medium text-gray-500">Due Date</th>
              <th className="text-center px-3 py-3 font-medium text-gray-500">Days Left</th>
              <th className="text-center px-3 py-3 font-medium text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 50).map((e: any, i: number) => (
              <tr key={i} className={`border-t border-gray-100 ${e.urgency === 'overdue' ? 'bg-red-50' : e.urgency === 'critical' ? 'bg-yellow-50' : ''}`}>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">{e.entity_type}</span>
                </td>
                <td className="px-3 py-3 font-medium">{e.entity_name}</td>
                <td className="px-3 py-3 text-gray-600">{e.label}</td>
                <td className="text-center px-3 py-3">{e.date}</td>
                <td className="text-center px-3 py-3">
                  <span className="font-medium" style={{ color: URGENCY_COLORS[e.urgency] || '#6b7280' }}>
                    {e.days_until != null ? (e.days_until < 0 ? `${Math.abs(e.days_until)}d overdue` : `${e.days_until}d`) : '—'}
                  </span>
                </td>
                <td className="text-center px-3 py-3">
                  <UrgencyBadge urgency={e.urgency} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">No events in this category</div>
        )}
      </div>
    </div>
  )
}


/* ═══════════════════════════════════════════════════
   DRIVER DEBRIEF PANEL
   ═══════════════════════════════════════════════════ */
function DebriefPanel({ data, drivers, onSelectDriver, selectedId }: {
  data: any; drivers: any[]; onSelectDriver: (id: number) => void; selectedId: number | null
}) {
  const [driverSearch, setDriverSearch] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const filteredDrivers = drivers.filter((d: any) => {
    if (!driverSearch) return true
    const s = driverSearch.toLowerCase()
    return (d.driver_name || '').toLowerCase().includes(s)
  })

  const selectedDriver = drivers.find((d: any) => d.driver_id === selectedId)

  return (
    <div className="space-y-6">
      {/* Driver selector — searchable dropdown */}
      <div className="bg-white rounded border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Driver for Debrief</h3>
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder={selectedDriver ? selectedDriver.driver_name : 'Search drivers...'}
            value={driverSearch}
            onChange={(e) => { setDriverSearch(e.target.value); setDropdownOpen(true) }}
            onFocus={() => setDropdownOpen(true)}
            className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-400 focus:border-transparent"
          />
          {selectedDriver && !driverSearch && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{
                backgroundColor: (GRADE_COLORS[selectedDriver.grade] || '#6b7280') + '20',
                color: GRADE_COLORS[selectedDriver.grade] || '#6b7280',
              }}>Score: {selectedDriver.score}</span>
            </div>
          )}
          {dropdownOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-64 overflow-y-auto">
                {filteredDrivers.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-400">No matching drivers</div>
                ) : (
                  filteredDrivers.map((d: any) => (
                    <button
                      key={d.driver_id}
                      onClick={() => {
                        onSelectDriver(d.driver_id)
                        setDriverSearch('')
                        setDropdownOpen(false)
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between ${
                        selectedId === d.driver_id ? 'bg-blue-50 font-medium' : ''
                      }`}
                    >
                      <span>{d.driver_name}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{
                        backgroundColor: (GRADE_COLORS[d.grade] || '#6b7280') + '20',
                        color: GRADE_COLORS[d.grade] || '#6b7280',
                      }}>{d.score}</span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {data && (
        <>
          {/* Debrief header */}
          <div className="bg-white rounded border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{data.driver_name}</h2>
                <p className="text-sm text-gray-500">Card: {data.card_number} | Debrief Date: {data.debrief_date}</p>
              </div>
              <div className="text-center">
                <span className="text-4xl font-bold" style={{ color: GRADE_COLORS[data.risk_score?.grade] }}>
                  {data.risk_score?.score}
                </span>
                <p className="text-xs text-gray-400">Risk Score</p>
              </div>
            </div>

            {/* Performance grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <MiniStat label="Driving Hours" value={`${data.performance_summary?.total_driving_hours || 0}h`} />
              <MiniStat label="Distance" value={`${(data.performance_summary?.total_distance_km || 0).toLocaleString()} km`} />
              <MiniStat label="Days Worked" value={data.performance_summary?.days_worked || 0} />
              <MiniStat label="DCRS Level" value={data.dcrs?.level || 1} />
            </div>
          </div>

          {/* Infringement summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Infringement Summary</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{data.performance_summary?.total_infringements || 0}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{data.performance_summary?.open_infringements || 0}</p>
                  <p className="text-xs text-gray-500">Open</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{data.performance_summary?.resolved_infringements || 0}</p>
                  <p className="text-xs text-gray-500">Resolved</p>
                </div>
              </div>
            </div>

            {/* Talking points */}
            <div className="bg-indigo-50 rounded border border-indigo-200 p-5">
              <h3 className="text-sm font-semibold text-indigo-700 mb-3">Suggested Talking Points</h3>
              {data.talking_points?.length > 0 ? (
                <div className="space-y-2">
                  {data.talking_points.map((tp: string, i: number) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-0.5">•</span>
                      <p className="text-sm text-indigo-700">{tp}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-indigo-400">No specific points — general check-in recommended</p>
              )}
            </div>
          </div>

          {/* Recent infringements detail */}
          {data.recent_infringements?.length > 0 && (
            <div className="bg-white rounded border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Infringements (Last 90 Days)</h3>
              <div className="space-y-2">
                {data.recent_infringements.map((inf: any, i: number) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded bg-gray-50 border border-gray-200">
                    <div>
                      <p className="text-sm font-medium">{(inf.type || '').replace(/_/g, ' ')}</p>
                      <p className="text-xs text-gray-500">{inf.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-700">{inf.date}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        inf.severity === 'very_serious' ? 'bg-red-100 text-red-700' :
                        inf.severity === 'serious' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{inf.severity || 'minor'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Risk score breakdown */}
          <div className="bg-white rounded border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Risk Score Breakdown</h3>
            <div className="space-y-3">
              {data.risk_score?.breakdown && Object.entries(data.risk_score.breakdown).map(([key, val]: [string, any]) => (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    <span className="font-medium">{val.score}/{val.max}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${(val.score / val.max) * 100}%`,
                        backgroundColor: val.score / val.max > 0.7 ? '#10b981' : val.score / val.max > 0.4 ? '#f59e0b' : '#ef4444',
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{val.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!data && !selectedId && (
        <EmptyState msg="Select a driver above to generate their debrief pack." />
      )}
    </div>
  )
}


/* ═══════════════════════════════════════════════════
   AUDIT PACK PANEL
   ═══════════════════════════════════════════════════ */
function AuditPanel({ data }: { data: any }) {
  if (!data) return <EmptyState msg="Loading audit pack..." />

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-50 rounded border border-slate-300 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800">DVSA Audit Pack</h2>
            <p className="text-sm text-slate-600">Generated: {new Date(data.generated_at).toLocaleString('en-GB')}</p>
            <p className="text-sm text-slate-600">By: {data.generated_by}</p>
          </div>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-slate-700 text-white rounded text-sm font-medium hover:bg-slate-800"
          >
            Print / Export
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MiniCard label="Fleet Risk Score" value={data.summary.fleet_risk_score} />
        <MiniCard label="Total Infringements" value={data.summary.total_infringements} />
        <MiniCard label="Resolution Rate" value={data.summary.resolution_rate} />
        <MiniCard label="Download Compliance" value={data.summary.download_compliance_rate} />
      </div>

      {/* Driver records */}
      <div className="bg-white rounded border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Driver Compliance Records</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Driver</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">Risk</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">Infringements</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">Open</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">DCRS</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">Download</th>
              </tr>
            </thead>
            <tbody>
              {data.drivers.map((d: any) => (
                <tr key={d.driver_id} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium">{d.driver_name}</td>
                  <td className="text-center px-3 py-2">
                    <span className="font-bold" style={{ color: GRADE_COLORS[d.risk_grade] }}>{d.risk_score} ({d.risk_grade})</span>
                  </td>
                  <td className="text-center px-3 py-2">{d.total_infringements}</td>
                  <td className="text-center px-3 py-2">
                    <span className={d.open_infringements > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>{d.open_infringements}</span>
                  </td>
                  <td className="text-center px-3 py-2">L{d.dcrs_level} ({d.dcrs_points}pts)</td>
                  <td className="text-center px-3 py-2">
                    {d.download_compliant ? (
                      <span className="text-green-600 text-xs font-medium">Compliant</span>
                    ) : (
                      <span className="text-red-600 text-xs font-medium">{d.days_since_download}d ago</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vehicle records */}
      <div className="bg-white rounded border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Vehicle Compliance Records</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Registration</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Vehicle</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">MOT</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">Tax</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">Tacho Cal</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">Insurance</th>
              </tr>
            </thead>
            <tbody>
              {data.vehicles.map((v: any) => (
                <tr key={v.vehicle_id} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium">{v.registration}</td>
                  <td className="px-3 py-2 text-gray-600">{v.make} {v.model}</td>
                  <td className="text-center px-3 py-2">{v.mot_expiry || '—'}</td>
                  <td className="text-center px-3 py-2">{v.tax_due_date || '—'}</td>
                  <td className="text-center px-3 py-2">{v.tacho_calibration_due || '—'}</td>
                  <td className="text-center px-3 py-2">{v.insurance_expiry || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Infringement log */}
      <div className="bg-white rounded border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Complete Infringement Log</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Date</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Driver</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Type</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">Severity</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">Status</th>
                <th className="text-center px-3 py-2 font-medium text-gray-500">Resolved</th>
              </tr>
            </thead>
            <tbody>
              {data.infringement_log.map((inf: any, i: number) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-3 py-2">{inf.date}</td>
                  <td className="px-3 py-2">{inf.driver_name}</td>
                  <td className="px-3 py-2">{(inf.type || '').replace(/_/g, ' ')}</td>
                  <td className="text-center px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      inf.severity === 'very_serious' ? 'bg-red-100 text-red-700' :
                      inf.severity === 'serious' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{inf.severity}</span>
                  </td>
                  <td className="text-center px-3 py-2">
                    <StatusBadge status={inf.status} />
                  </td>
                  <td className="text-center px-3 py-2 text-xs text-gray-500">{inf.resolved_at ? new Date(inf.resolved_at).toLocaleDateString('en-GB') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}


/* ═══════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════ */

function UrgencyBadge({ urgency }: { urgency: string }) {
  const labels: Record<string, string> = {
    exceeded: 'Exceeded', critical: 'Critical', overdue: 'Overdue',
    low: 'Low', warning: 'Warning', moderate: 'Moderate',
    good: 'Good', ok: 'OK', excellent: 'Excellent', unknown: 'Unknown',
    upcoming: 'Upcoming',
  }
  const color = URGENCY_COLORS[urgency] || '#9ca3af'
  return (
    <span
      className="text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ backgroundColor: color + '20', color }}
    >
      {labels[urgency] || urgency}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    open: { bg: 'bg-red-100', text: 'text-red-700' },
    under_review: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
    disputed: { bg: 'bg-purple-100', text: 'text-purple-700' },
    resolved_no_action: { bg: 'bg-blue-100', text: 'text-blue-700' },
    resolved_action_taken: { bg: 'bg-green-100', text: 'text-green-700' },
    closed: { bg: 'bg-gray-100', text: 'text-gray-700' },
  }
  const c = colors[status] || colors.open
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.bg} ${c.text}`}>
      {(status || 'open').replace(/_/g, ' ')}
    </span>
  )
}

function StatRow({ label, value, color }: { label: string; value: any; color?: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium" style={color ? { color } : {}}>{value}</span>
    </div>
  )
}

function AlertCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colors: Record<string, { bg: string; border: string; text: string }> = {
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
    green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
    gray: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' },
  }
  const c = colors[color] || colors.gray
  return (
    <div className={`rounded border p-4 ${c.bg} ${c.border}`}>
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${c.text}`}>{value}</p>
    </div>
  )
}

function MiniStat({ label, value }: { label: string; value: any }) {
  return (
    <div className="bg-gray-50 rounded p-3 text-center">
      <p className="text-lg font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  )
}

function MiniCard({ label, value }: { label: string; value: any }) {
  return (
    <div className="bg-white rounded border border-gray-200 p-4 text-center">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  )
}

function PaginationBar({ pagination, onPageChange }: { pagination: any; onPageChange: (p: number) => void }) {
  const { page, total_pages, total } = pagination
  if (total_pages <= 1) return null

  // Show at most 7 page buttons
  const pages: number[] = []
  const start = Math.max(1, page - 3)
  const end = Math.min(total_pages, page + 3)
  for (let i = start; i <= end; i++) pages.push(i)

  return (
    <div className="flex items-center justify-between mt-4 px-1">
      <span className="text-xs text-gray-500">{total} results - page {page} of {total_pages}</span>
      <div className="flex items-center gap-1">
        <button
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="px-2 py-1 text-xs rounded border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50"
        >
          Prev
        </button>
        {pages.map(p => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`w-8 h-8 text-xs rounded border ${
              p === page ? 'bg-slate-700 text-white border-slate-700' : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            {p}
          </button>
        ))}
        <button
          disabled={page >= total_pages}
          onClick={() => onPageChange(page + 1)}
          className="px-2 py-1 text-xs rounded border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  )
}

function EmptyState({ msg }: { msg: string }) {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      </div>
      <p className="text-gray-400 text-sm">{msg}</p>
    </div>
  )
}
